<template>
  <Popover>
    <PopoverTrigger as-child>
      <Button variant="outline" role="combobox" class="w-full justify-start text-left h-11 md:h-auto md:min-h-10 px-3 md:py-2 overflow-hidden md:overflow-visible">
        
        <!-- Mobile Limited View -->
        <div class="flex md:hidden items-center gap-1.5 w-full">
          <template v-if="selected.length > 0">
            <Badge v-for="id in selected.slice(0, 1)" :key="'mobile-'+id" variant="secondary" class="truncate max-w-[100px] block">
              {{ regions.find(r => r.id === id)?.name || id }}
            </Badge>
            <Badge v-if="selected.length > 1" variant="secondary" class="flex-shrink-0">
              +{{ selected.length - 1 }}
            </Badge>
          </template>
          <template v-else>
            <span class="text-muted-foreground font-normal truncate">Select regions</span>
          </template>
        </div>

        <!-- Desktop Wrap View -->
        <div class="hidden md:flex flex-wrap gap-1">
          <template v-if="selected.length > 0">
            <Badge v-for="id in selected" :key="'desktop-'+id" variant="secondary" class="mr-1">
              {{ regions.find(r => r.id === id)?.name || id }}
            </Badge>
          </template>
          <template v-else>
            <span class="text-muted-foreground font-normal">Select regions</span>
          </template>
        </div>

      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-[300px] p-0 z-[99999]" align="start">
      <div class="max-h-[300px] overflow-y-auto p-1">
        <div 
          v-for="region in regions" 
          :key="region.id"
          class="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground cursor-pointer"
          @click="toggleSelection(region.id)"
        >
          <div class="mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary overflow-hidden" :class="selected.includes(region.id) ? 'bg-primary text-primary-foreground' : 'opacity-50'">
            <Check v-if="selected.includes(region.id)" class="h-3 w-3" />
          </div>
          {{ region.name }}
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Check } from 'lucide-vue-next'

interface RegionOption {
  id: string
  name: string
}

const props = defineProps<{
  regions: RegionOption[]
  modelValue: string[]
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
}>()

const selected = ref<string[]>(props.modelValue.filter(r => r.toLowerCase() !== 'world'))

function toggleSelection(id: string) {
  const index = selected.value.indexOf(id)
  if (index === -1) {
    selected.value.push(id)
  } else {
    selected.value.splice(index, 1)
  }
}

watch(selected, (newVal) => {
  const filtered = newVal.filter(r => r.toLowerCase() !== 'world')
  if (JSON.stringify(filtered) !== JSON.stringify(props.modelValue)) {
    emit('update:modelValue', [...filtered])
  }
}, { deep: true })

watch(
  () => props.modelValue,
  (newVal) => {
    const filtered = newVal.filter(r => r.toLowerCase() !== 'world')
    if (JSON.stringify(filtered) !== JSON.stringify(selected.value)) {
      selected.value = [...filtered]
    }
  }, { deep: true }
)
</script>